type UnknownRecord = Record<string, unknown>;

export type TWarboxMessageMedia = {
    url: string;
    contentType?: string;
    filename?: string;
};

const isRecord = (value: unknown): value is UnknownRecord =>
    typeof value === 'object' && value !== null;

const getString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() ? value.trim() : undefined;

const getDateValue = (value: unknown): Date | string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string' || value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);

    return undefined;
};

export const getWarboxMessageMediaItems = (
    message: unknown
): TWarboxMessageMedia[] => {
    if (!isRecord(message)) return [];

    const content = message.content;

    if (isRecord(content) && Array.isArray(content.media)) {
        return content.media
            .map((item): TWarboxMessageMedia | null => {
                if (!isRecord(item)) return null;

                const url = getString(item.url);
                if (!url) return null;

                return {
                    url,
                    contentType: getString(item.contentType),
                    filename: getString(item.filename),
                };
            })
            .filter((item): item is TWarboxMessageMedia => Boolean(item));
    }

    const legacyUrl =
        getString(message.MediaUrl0) ||
        getString(message.mediaUrl) ||
        (isRecord(message.media) ? getString(message.media.url) : undefined);

    if (!legacyUrl) return [];

    return [
        {
            url: legacyUrl,
            contentType:
                getString(message.MediaContentType0) ||
                getString(message.mediaContentType) ||
                (isRecord(message.media)
                    ? getString(message.media.contentType)
                    : undefined),
        },
    ];
};

export const warboxMessageHasMedia = (message: unknown): boolean =>
    getWarboxMessageMediaItems(message).length > 0;

export const getWarboxMessageOrigin = (
    message: unknown
): 'warbox' | 'customer' => {
    if (!isRecord(message)) return 'customer';

    const direction = String(message.direction || message.Direction || '')
        .toLowerCase()
        .trim();

    return direction.includes('out') ? 'warbox' : 'customer';
};

export const getWarboxMessageId = (message: unknown): string => {
    if (!isRecord(message)) return String(Date.now());

    return (
        getString(message._id) ||
        getString(message.id) ||
        getString(message.wspId) ||
        String(message.createdAt || message.created_at || Date.now())
    );
};

export const getWarboxMessageCreatedAt = (
    message: unknown
): Date | string => {
    if (!isRecord(message)) return new Date();

    return (
        getDateValue(message.createdAt) ||
        getDateValue(message.created_at) ||
        new Date()
    );
};
